import { recibosCobroAPI, recibosCobroGrupoAPI, pagosAPI } from '../services/api';
import { imprimirReciboDual, imprimirReciboGrupoDual, imprimirPagoDual, imprimirMovimientoDual } from './comprobantePrint';
import { buildPrintHeaderHtml, estilosPrintHeader, getPrintLogoUrl, getPrintEncabezado } from './printBranding';
import { textoOrigenDestino } from './fondosLabels';
import { esMovimientoPagoCuenta } from './comprobantePago';
import { formatFechaSistema } from './fechasSistema';

export { esMovimientoPagoCuenta };

const formatMoneda = (valor) =>
  parseFloat(valor || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 });

export function movimientoTienePago(mov) {
  if (esMovimientoAnulacionPago(mov)) return false;
  return Boolean(mov?.pago_id || mov?.numero_pago || mov?.numero_pago_cuenta);
}

export function esMovimientoAnulacionRecibo(mov) {
  if (!mov) return false;
  const predef = (mov.movimiento_predefinido_descripcion || '').toLowerCase();
  if (predef.includes('anulación recibo') || predef.includes('anulacion recibo')) return true;
  const concepto = (mov.concepto || '').toLowerCase();
  return (
    mov.tipo_movimiento === 'DEBITO' &&
    (concepto.includes('anulación recibo') || concepto.includes('anulacion recibo'))
  );
}

export function esMovimientoAnulacionPago(mov) {
  if (!mov) return false;
  const predef = (mov.movimiento_predefinido_descripcion || '').toLowerCase();
  if (
    predef.includes('anulación orden de pago') || predef.includes('anulacion orden de pago') ||
    predef.includes('anulación pago a cuenta') || predef.includes('anulacion pago a cuenta')
  ) return true;
  const concepto = (mov.concepto || '').toLowerCase();
  return (
    mov.tipo_movimiento === 'CREDITO' &&
    (
      concepto.includes('anulación orden de pago') || concepto.includes('anulacion orden de pago') ||
      concepto.includes('anulación pago a cuenta') || concepto.includes('anulacion pago a cuenta')
    )
  );
}

export function movimientoTieneReciboGrupo(mov) {
  if (esMovimientoAnulacionRecibo(mov)) return false;
  return Boolean(mov?.recibo_cobro_grupo_id);
}

export function movimientoTieneRecibo(mov) {
  if (esMovimientoAnulacionRecibo(mov)) return false;
  if (mov?.recibo_cobro_grupo_id) return false;
  return Boolean(mov?.recibo_cobro_id || mov?.numero_recibo);
}

export async function imprimirReciboGrupoPorMovimiento(mov, formatFecha, logoUrl) {
  const url = logoUrl || getPrintLogoUrl();
  if (!mov?.recibo_cobro_grupo_id) return false;
  try {
    const res = await recibosCobroGrupoAPI.getById(mov.recibo_cobro_grupo_id);
    if (res.success && res.data) {
      imprimirReciboGrupoDual(res.data, formatFecha, url);
      return true;
    }
  } catch (_) {
    /* sin recibo grupo */
  }
  return false;
}

export async function imprimirReciboPorMovimiento(mov, formatFecha, logoUrl) {
  const url = logoUrl || getPrintLogoUrl();
  if (mov?.recibo_cobro_id) {
    try {
      const res = await recibosCobroAPI.getById(mov.recibo_cobro_id);
      if (res.success && res.data) {
        imprimirReciboDual(res.data, formatFecha, url);
        return true;
      }
    } catch (_) {
      /* intentar por movimiento */
    }
  }
  try {
    const res = await recibosCobroAPI.getByMovimientoId(mov.id);
    if (res.success && res.data) {
      imprimirReciboDual(res.data, formatFecha, url);
      return true;
    }
  } catch (_) {
    /* sin recibo */
  }
  return false;
}

export async function imprimirPagoPorMovimiento(mov, formatFecha, logoUrl) {
  const res = await pagosAPI.getByMovimientoId(mov.id);
  if (res.success && res.data) {
    imprimirPagoDual(res.data, formatFecha, logoUrl);
    return true;
  }
  return false;
}

async function cargarDetalleComprobante(mov) {
  if (movimientoTieneReciboGrupo(mov)) {
    try {
      const res = await recibosCobroGrupoAPI.getById(mov.recibo_cobro_grupo_id);
      if (res.success && res.data) return { reciboGrupo: res.data };
    } catch (_) { /* ignore */ }
  }
  if (movimientoTieneRecibo(mov)) {
    if (mov.recibo_cobro_id) {
      const res = await recibosCobroAPI.getById(mov.recibo_cobro_id);
      if (res.success && res.data) return { recibo: res.data };
    }
    try {
      const res = await recibosCobroAPI.getByMovimientoId(mov.id);
      if (res.success && res.data) return { recibo: res.data };
    } catch (_) { /* ignore */ }
  }
  if (movimientoTienePago(mov)) {
    const res = await pagosAPI.getByMovimientoId(mov.id);
    if (res.success && res.data) return { pago: res.data };
  }
  return {};
}

/** Impresión unificada: recibo, OP o comprobante de movimiento (mismo formato en todos los listados). */
export async function imprimirComprobanteMovimiento(mov, formatFecha, logoUrl) {
  const url = logoUrl || getPrintLogoUrl();
  if (movimientoTieneReciboGrupo(mov)) {
    return imprimirReciboGrupoPorMovimiento(mov, formatFecha, url);
  }
  if (movimientoTieneRecibo(mov)) {
    return imprimirReciboPorMovimiento(mov, formatFecha, url);
  }
  if (movimientoTienePago(mov)) {
    return imprimirPagoPorMovimiento(mov, formatFecha, url);
  }
  const detalle = await cargarDetalleComprobante(mov);
  imprimirMovimientoDual(mov, formatFecha, { logoUrl: url, ...detalle });
  return true;
}

/** @deprecated Usar imprimirComprobanteMovimiento */
export async function abrirPdfMovimiento(movimientoId) {
  throw new Error('Use imprimirComprobanteMovimiento con el objeto movimiento');
}

export function imprimirReporteMovimientos({
  titulo,
  subtitulo,
  entidadInfoHtml,
  movimientos,
  formatFecha,
  saldoFinal,
  periodoHtml = '',
  logoUrl,
}) {
  const fmt = formatFecha || ((f) => formatFechaSistema(f));
  const logo = logoUrl || getPrintLogoUrl();
  const rows = (movimientos || []).map((m) => `
    <tr>
      <td>${fmt(m.fecha)}</td>
      <td>${(m.concepto || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>
      <td style="text-align:right">${m.tipo_movimiento === 'DEBITO' ? formatMoneda(m.monto) : '-'}</td>
      <td style="text-align:right">${m.tipo_movimiento === 'CREDITO' ? formatMoneda(m.monto) : '-'}</td>
      <td style="text-align:right;font-weight:bold">${formatMoneda(m.saldo_acumulado)}</td>
    </tr>
  `).join('');

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>${titulo}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; color: #333; }
        ${estilosPrintHeader}
        .subtitulo { color: #666; margin-bottom: 20px; font-size: 11px; }
        .entidad-info { margin-bottom: 20px; padding: 12px; background: #f5f5f5; border-radius: 6px; }
        .entidad-info p { margin: 4px 0; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background: #2c3e50; color: white; font-size: 11px; }
        td { font-size: 11px; }
        .saldo-final { margin-top: 16px; padding: 10px; background: #e8f4f8; font-weight: bold; text-align: right; }
        @media print { body { padding: 10px; } }
      </style>
    </head>
    <body>
      ${buildPrintHeaderHtml({
        titulo,
        logoUrl: logo,
        encabezado: getPrintEncabezado(),
      })}
      <div class="subtitulo">${subtitulo || ''}</div>
      <div class="entidad-info">${entidadInfoHtml}${periodoHtml}</div>
      <table>
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Concepto</th>
            <th style="text-align:right">Débito</th>
            <th style="text-align:right">Crédito</th>
            <th style="text-align:right">Saldo acumulado</th>
          </tr>
        </thead>
        <tbody>
          ${rows || '<tr><td colspan="5">Sin movimientos en el período</td></tr>'}
        </tbody>
      </table>
      <div class="saldo-final">Saldo al cierre: $${formatMoneda(saldoFinal)}</div>
      <script>window.onload = function() { window.print(); };</script>
    </body>
    </html>
  `;

  const ventana = window.open('', '_blank');
  ventana.document.write(html);
  ventana.document.close();
}

export function imprimirResumenMovimientoFondo(mf, formatFecha, logoUrl) {
  const fmt = formatFecha || ((f) => formatFechaSistema(f));
  const logo = logoUrl || getPrintLogoUrl();
  const det = mf.detalles || [];
  const detalleHtml = det.length
    ? det.map((d) => {
        if (d.tipo === 'caja') return `Caja: ${d.caja_nombre || d.caja_id} — $${formatMoneda(d.monto)}`;
        if (d.tipo === 'cheque_tercero') return `Cheque Nº ${d.cheque_numero || '-'} — $${formatMoneda(d.monto || d.cheque_importe)}`;
        if (d.tipo === 'cuenta') return `Cuenta — $${formatMoneda(d.monto)}`;
        return `${d.tipo} — $${formatMoneda(d.monto)}`;
      }).join('<br/>')
    : '—';

  const origen = mf.origen_label || textoOrigenDestino(mf).split(' → ')[0] || '—';
  const destino = mf.destino_label || textoOrigenDestino(mf).split(' → ')[1] || '—';
  const clase = mf.clase_label || mf.clase_movimiento || mf.tipo || '';
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Movimiento de fondos #${mf.id}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 11px; padding: 16px; }
        ${estilosPrintHeader}
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
        th { background: #2c3e50; color: #fff; width: 30%; }
      </style>
    </head>
    <body>
      ${buildPrintHeaderHtml({
        titulo: 'Movimiento de fondos',
        logoUrl: logo,
        encabezado: getPrintEncabezado(),
        fecha: fmt(mf.fecha),
        numeroDocumento: mf.id,
        numeroLabel: 'N°',
      })}
      <table>
        <tr><th>Fecha</th><td>${fmt(mf.fecha)}</td></tr>
        <tr><th>Origen</th><td>${String(origen).replace(/</g, '&lt;')}</td></tr>
        <tr><th>Destino</th><td>${String(destino).replace(/</g, '&lt;')}</td></tr>
        <tr><th>Clase</th><td>${clase}</td></tr>
        <tr><th>Concepto</th><td>${(mf.concepto || '').replace(/</g, '&lt;')}</td></tr>
        <tr><th>Monto</th><td>$${formatMoneda(mf.monto_total)}</td></tr>
        <tr><th>Detalle ítems</th><td>${detalleHtml}</td></tr>
        <tr><th>Usuario</th><td>${mf.usuario_nombre || '—'}</td></tr>
      </table>
      <script>window.onload = function() { window.print(); };</script>
    </body>
    </html>
  `;
  const ventana = window.open('', '_blank');
  ventana.document.write(html);
  ventana.document.close();
}
